import React from "react"
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from "@dnd-kit/core"
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable, arrayMove,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { GripVertical, Copy, Trash2 } from "lucide-react"
import { Button } from "src/components/ui/button"
import { BLOCK_DEF_MAP } from "./blockSchema"
import { BlockRenderer } from "./BlockRenderer"

function SortableBlock({ block, selected, onSelect, onDuplicate, onDelete, imagesById, project }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id })
  const def = BLOCK_DEF_MAP[block.type]
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={() => onSelect(block.id)}
      className={`group relative rounded-lg border bg-white p-3 ${
        selected ? "border-slate-900 ring-1 ring-slate-900" : "border-slate-200 hover:border-slate-300"
      }`}
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-auto w-auto cursor-grab touch-none p-0 text-slate-300 hover:bg-transparent hover:text-slate-500"
            aria-label="Drag to reorder"
            {...attributes}
            {...listeners}
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="h-4 w-4" />
          </Button>
          {def?.label || block.type}
        </span>
        <span className="flex items-center gap-0.5 opacity-0 transition group-hover:opacity-100">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            title="Duplicate"
            onClick={(e) => { e.stopPropagation(); onDuplicate(block.id) }}
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500"
            title="Delete"
            onClick={(e) => { e.stopPropagation(); onDelete(block.id) }}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </span>
      </div>
      <div className="pointer-events-none">
        <BlockRenderer block={block} imagesById={imagesById} project={project} />
      </div>
    </div>
  )
}

export function BlockList({ blocks, selectedId, onSelect, onReorder, onDuplicate, onDelete, imagesById, project }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  function handleDragEnd(event) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = blocks.findIndex((b) => b.id === active.id)
    const newIndex = blocks.findIndex((b) => b.id === over.id)
    if (oldIndex < 0 || newIndex < 0) return
    onReorder(arrayMove(blocks, oldIndex, newIndex))
  }

  if (blocks.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center rounded-lg border border-dashed border-slate-200 text-[13px] text-slate-400">
        Add blocks from the palette to build the page.
      </div>
    )
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-2">
          {blocks.map((block) => (
            <SortableBlock
              key={block.id}
              block={block}
              selected={block.id === selectedId}
              onSelect={onSelect}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              imagesById={imagesById}
              project={project}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}
